<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SalesReviewPageHeader from '../components/SalesReviewPageHeader.vue';
import SalesReviewPartySection from '../components/SalesReviewPartySection.vue';
import { useSalesReview } from '../composables/useSalesReview';
import {
  isSalesReviewPartyRole,
  salesReviewPartyCount,
  salesReviewPartyGroups,
} from '../composables/salesReviewPartyGroups';
import {
  salesReviewLocation,
  salesReviewReturnQuery,
} from '../composables/salesReviewNavigation';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const transactionId = computed(() => route.params.salesTransactionId);
const partyRole = computed(() => String(route.params.partyRole || ''));
const validRole = computed(() => isSalesReviewPartyRole(partyRole.value));
const review = useSalesReview({
  id: computed(() => (validRole.value ? transactionId.value : null)),
  mode: computed(() => 'correction'),
  notify: useAlert,
  t,
});
const groups = computed(() =>
  salesReviewPartyGroups(
    review.participants.value,
    review.items.value,
    partyRole.value
  )
);
const roleLabel = count => {
  if (partyRole.value === 'vendedor') {
    return t('SALES.REVIEW.PARTIES.SELLERS', count);
  }

  return t('SALES.REVIEW.PARTIES.BUYERS', count);
};
const countLabel = count =>
  t('SALES.REVIEW.PARTIES.COUNT', { count, role: roleLabel(count) });
const goBack = () =>
  router.push(
    salesReviewLocation({
      accountId: route.params.accountId,
      salesTransactionId: transactionId.value,
    })
  );
const openParty = card => {
  const params = {
    accountId: route.params.accountId,
    salesTransactionId: transactionId.value,
    partyRole: partyRole.value,
    partyId: card.party.id,
  };
  if (card.representative) {
    router.push({
      name: 'operacao_imobiliaria_vendas_cadastro_representante',
      params: { ...params, parentPartyId: card.party.parent_party_id },
      query: salesReviewReturnQuery,
    });
    return;
  }
  router.push({
    name: 'operacao_imobiliaria_vendas_cadastro_parte',
    params: {
      ...params,
      partyKind: card.party.kind.replace('_', '-'),
    },
    query: salesReviewReturnQuery,
  });
};
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <SalesReviewPageHeader
      :can-submit="false"
      is-correction
      read-only
      :saving="false"
      :transaction="review.transaction.value"
      @back="goBack"
    />

    <main
      class="mx-auto grid w-full max-w-[1500px] gap-6 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <p
        v-if="!validRole"
        class="rounded-lais border border-lais-danger bg-red-50 p-4 text-sm text-lais-danger"
      >
        {{ t('SALES.REVIEW.PARTIES.INVALID_ROLE') }}
      </p>
      <p
        v-else-if="review.loading.value"
        class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
      >
        {{ t('SALES.COMMON.LOADING') }}
      </p>
      <template v-else-if="review.transaction.value">
        <SalesReviewPartySection
          :companies="[]"
          :count-label="countLabel(groups.people.length)"
          :people="groups.people"
          :title="t('SALES.REVIEW.PARTIES.PHYSICAL')"
          @open="openParty"
        />
        <SalesReviewPartySection
          :companies="groups.companies"
          :count-label="
            countLabel(
              salesReviewPartyCount({
                companies: groups.companies,
                people: [],
              })
            )
          "
          :title="t('SALES.REVIEW.PARTIES.LEGAL')"
          @open="openParty"
        />
      </template>
    </main>
  </div>
</template>
